// metaPixel.js — Meta Pixel (browser) + Conversions API (server) bridge.
//
// TRACKING / ATTRIBUTION ONLY. Everything here is inert unless the backend
// reports `enabled: true` from GET /api/marketing/pixel-config (which itself
// requires the admin `pixel_enabled` safety switch to be ON). No ad spend.
//
// Every conversion is sent twice with the SAME event_id:
//   1) Browser Pixel  → window.fbq('track', name, customData, { eventID })
//   2) Server CAPI     → POST /api/marketing/capi/event (backend hashes PII)
// Meta deduplicates the pair via the shared event_id.

import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';

const API = process.env.REACT_APP_BACKEND_URL;

// Module-level runtime config, set once after the backend responds.
let _cfg = { enabled: false, pixel_id: '', test_event_code: '' };
let _pixelLoaded = false;

// ── helpers ──────────────────────────────────────────────────────────────
function generateEventId() {
  try {
    if (window.crypto && window.crypto.randomUUID) return window.crypto.randomUUID();
  } catch { /* fall through */ }
  return `e-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}

function getCookie(name) {
  const m = document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]*)`));
  return m ? decodeURIComponent(m[1]) : null;
}

function getFbp() {
  return getCookie('_fbp');
}

// Build fbc from the fbclid query param (Meta format fb.1.<ts>.<fbclid>),
// persisting it so later in-SPA events still carry it after the param is gone.
function getFbc() {
  const existing = getCookie('_fbc');
  if (existing) return existing;
  try {
    const fbclid = new URLSearchParams(window.location.search).get('fbclid');
    if (fbclid) {
      const val = `fb.1.${Math.floor(Date.now() / 1000)}.${fbclid}`;
      document.cookie = `_fbc=${val}; path=/; max-age=${60 * 60 * 24 * 90}`;
      return val;
    }
  } catch { /* ignore */ }
  return null;
}

function loadPixel(pixelId) {
  if (_pixelLoaded || !pixelId) return;
  _pixelLoaded = true;
  /* eslint-disable */
  !(function (f, b, e, v, n, t, s) {
    if (f.fbq) return; n = f.fbq = function () {
      n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments);
    };
    if (!f._fbq) f._fbq = n; n.push = n; n.loaded = !0; n.version = '2.0';
    n.queue = []; t = b.createElement(e); t.async = !0;
    t.src = 'https://connect.facebook.net/en_US/fbevents.js';
    s = b.getElementsByTagName(e)[0]; s.parentNode.insertBefore(t, s);
  })(window, document, 'script');
  /* eslint-enable */
  window.fbq('init', pixelId);
}

// ── public API ─────────────────────────────────────────────────────────
// Fire a standard event to both Pixel and CAPI. No-op until enabled.
export function trackMetaEvent(eventName, { userData = null, customData = null } = {}) {
  if (!_cfg.enabled) return;
  const eventId = generateEventId();
  const sourceUrl = window.location.href;

  // 1) Browser Pixel
  try {
    if (window.fbq) {
      const opts = { eventID: eventId };
      if (_cfg.test_event_code) opts.test_event_code = _cfg.test_event_code;
      window.fbq('track', eventName, customData || {}, opts);
    }
  } catch { /* pixel best-effort */ }

  // 2) Server Conversions API (backend hashes PII)
  try {
    const body = JSON.stringify({
      event_name: eventName,
      event_id: eventId,
      event_source_url: sourceUrl,
      user_data: userData || {},
      custom_data: customData || {},
      fbp: getFbp(),
      fbc: getFbc(),
    });
    fetch(`${API}/api/marketing/capi/event`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body,
      keepalive: true,
    }).catch(() => {});
  } catch { /* capi best-effort */ }
}

// Convenience wrappers for the standard events.
export const metaTrack = {
  viewContent: (cd) => trackMetaEvent('ViewContent', { customData: cd }),
  lead: (ud, cd) => trackMetaEvent('Lead', { userData: ud, customData: cd }),
  contact: (ud, cd) => trackMetaEvent('Contact', { userData: ud, customData: cd }),
  startTrial: (cd) => trackMetaEvent('StartTrial', { customData: cd }),
  completeRegistration: (ud, cd) => trackMetaEvent('CompleteRegistration', { userData: ud, customData: cd }),
  schedule: (ud, cd) => trackMetaEvent('Schedule', { userData: ud, customData: cd }),
};

// Route prefixes that should additionally emit a ViewContent on navigation.
const VIEW_CONTENT_PREFIXES = ['/features', '/personal/features', '/business/features', '/government/features', '/pricing', '/personal/pricing', '/business/pricing', '/government/pricing'];

// Mount ONCE inside the Router. Loads config, fires PageView on every route
// change, and ViewContent on key marketing routes.
export function MetaPixelRouter() {
  const location = useLocation();
  const ready = useRef(false);

  // Load config once on mount.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${API}/api/marketing/pixel-config`);
        if (!res.ok) return;
        const data = await res.json();
        if (cancelled || !data.enabled || !data.pixel_id) return;
        _cfg = { enabled: true, pixel_id: data.pixel_id, test_event_code: data.test_event_code || '' };
        loadPixel(data.pixel_id);
        ready.current = true;
        if (window.fbq) window.fbq('track', 'PageView');
      } catch { /* tracking disabled / unreachable */ }
    })();
    return () => { cancelled = true; };
  }, []);

  // PageView (+ ViewContent on marketing routes) on every navigation.
  useEffect(() => {
    if (!_cfg.enabled || !ready.current || !window.fbq) return;
    window.fbq('track', 'PageView');
    const p = location.pathname.replace(/^\/(ja|en)(?=\/|$)/, '') || '/';
    if (VIEW_CONTENT_PREFIXES.some((pre) => p === pre || p.startsWith(pre + '/'))) {
      trackMetaEvent('ViewContent', { customData: { path: p } });
    }
  }, [location.pathname]);

  return null;
}
